import React from 'react';
import { useLocation } from 'react-router-dom';

const isChunkError = (error) => /Loading chunk|ChunkLoadError|dynamically imported module/i.test(String(error?.message || error));

class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.pathname !== this.props.pathname) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    console.error('UI error caught by ErrorBoundary:', error, info?.componentStack);
    // A new deployment replaced the old files: reload once to fetch the fresh version
    if (isChunkError(error) && !sessionStorage.getItem('chunk-reload')) {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white border border-[#E8DED1] p-6 text-center">
          <h2 className="text-lg font-semibold text-[#5D4E3C] mb-2">Hier ist etwas schiefgelaufen</h2>
          <p className="text-sm text-gray-600 mb-4">
            Dieser Bereich konnte nicht angezeigt werden. Bitte lade die Seite neu – deine Daten sind nicht verloren.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#8B7355] text-white text-sm hover:bg-[#6D5A47] transition-colors"
              data-testid="error-boundary-reload"
            >
              Seite neu laden
            </button>
            <a href={this.props.homeHref} className="px-4 py-2 border border-[#8B7355] text-[#8B7355] text-sm hover:bg-[#FAF5EE] transition-colors" data-testid="error-boundary-home">
              Zur Startseite
            </a>
          </div>
          <details className="mt-4 text-left">
            <summary className="text-xs text-gray-400 cursor-pointer">Technische Details</summary>
            <pre className="mt-2 text-[11px] text-red-700 whitespace-pre-wrap break-words" data-testid="error-boundary-details">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

// Resets automatically when the user navigates to another page
export const ErrorBoundary = ({ children, homeHref = '/' }) => {
  const { pathname } = useLocation();
  return <Boundary pathname={pathname} homeHref={homeHref}>{children}</Boundary>;
};
